import { ArrowRight } from "lucide-react";

import { useReadiness } from "../../hooks/useReadiness";
import type { DashboardSnapshot } from "../../lib/dashboard";
import type { HomeBestOffer } from "../../lib/home-best-offer";
import type { HomeHeroState } from "../../lib/home-hero-state";
import { getMarketingUrl } from "../../lib/marketing-url";
import { cn } from "../../lib/utils";
import { AvatarStack } from "../marketplace/AvatarStack";
import { CancelConnectionDialog } from "../marketplace/CancelConnectionDialog";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/feedback";
import { ReadinessChip } from "./ReadinessChip";

/**
 * Plan 435 M2 (mfh-hero-component). The ink-panel hero (`bg-panel-ink
 * rounded-3xl`) that replaces the M1 `OverviewView` stand-in inside
 * `HomeView`. Three variants, all sharing row 1 (eyebrow + `ReadinessChip`):
 *
 *   - `skeleton`   — gate `loading`. No copy, no CTAs, pulse placeholders.
 *   - `readiness`  — gate `off`. Today's readiness-only content (score
 *                    headline, verification dots via the chip, "Verify an
 *                    input" CTA) — no marketplace copy, no publish CTA.
 *   - `marketplace`— gate `on`, settled. The real `HomeHeroState` from
 *                    `deriveHomeHeroState`.
 *
 * `finalizing_escape`'s secondary CTA reuses the EXACT
 * `CancelConnectionDialog` `MarketplaceStatusCard` uses — never a forked
 * dialog. `amber` is a generic capability (M4's recovery-window variant
 * flips it); no M2 state sets it.
 */
export interface HomeHeroProps {
  snapshot: Pick<DashboardSnapshot, "financialInputs">;
  amber?: boolean;
}

export interface HomeHeroSkeletonProps extends HomeHeroProps {
  variant: "skeleton";
}

export interface HomeHeroReadinessProps extends HomeHeroProps {
  variant: "readiness";
  onVerify: () => void;
}

export interface HomeHeroMarketplaceProps extends HomeHeroProps {
  variant: "marketplace";
  state: HomeHeroState;
  onPrimary?: () => void;
  onSecondary?: () => void;
  onGuideClick?: () => void;
  onCancelConnection?: (connectionId: string) => Promise<void>;
  onChipClick?: () => void;
  /** `live_offers` only — the strongest offer in the inbox
   *  (`pickHomeBestOffer`), rendered as a highlighted, tappable line in
   *  place of the generic sub-copy. */
  bestOffer?: HomeBestOffer | null;
  onBestOffer?: (href: string) => void;
}

export type HomeHeroComponentProps =
  HomeHeroSkeletonProps | HomeHeroReadinessProps | HomeHeroMarketplaceProps;

export function HomeHero(props: HomeHeroComponentProps) {
  const { snapshot, amber = false } = props;
  const heroStateAttr =
    props.variant === "marketplace" ? props.state.kind : props.variant;

  return (
    <div
      className={cn(
        "rounded-3xl px-5 py-6 text-panel-ink-foreground sm:px-8 sm:py-8",
        amber
          ? "bg-amber-950"
          : "bg-panel-ink bg-linear-to-br from-panel-ink to-panel-ink-end",
      )}
      data-amber={amber || undefined}
      data-hero-state={heroStateAttr}
      data-testid="home-hero"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <p
          className={cn(
            "eyebrow-label shrink-0 whitespace-nowrap text-[11px] font-bold tracking-[0.18em]",
            // Mint on the ink panel; amber's own light tone on the recovery
            // variant (mint on brown read as a stray brand colour).
            amber ? "text-amber-200" : "text-panel-ink-accent",
          )}
          data-testid="home-hero-eyebrow"
        >
          {props.variant === "marketplace" ? props.state.eyebrow : "READINESS"}
        </p>
        <ReadinessChip
          className="shrink-0"
          onClick={
            props.variant === "marketplace" ? props.onChipClick : undefined
          }
          snapshot={snapshot}
        />
      </div>

      {props.variant === "skeleton" ? (
        <HomeHeroSkeletonBody />
      ) : props.variant === "readiness" ? (
        <HomeHeroReadinessBody onVerify={props.onVerify} />
      ) : (
        <HomeHeroMarketplaceBody {...props} />
      )}
    </div>
  );
}

// On-ink primary: the light pill from the design (dark text on near-white).
// A plain <button> because the DS Button's `cn` is a join, not a
// tailwind-merge — a className can't override its variant background.
const HERO_PRIMARY_CLASS =
  "inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-panel-ink-foreground px-6 text-base font-semibold text-panel-ink transition-[background-color,scale] hover:bg-panel-ink-foreground/90 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-panel-ink-accent focus-visible:ring-offset-2 focus-visible:ring-offset-panel-ink";

// Transparent on-ink secondary: same height/shape as the default DS
// Button so the CTA pair reads as one row, with a hairline border for a
// clear hit target instead of a bare text link.
const HERO_SECONDARY_CLASS =
  "inline-flex min-h-12 items-center justify-center rounded-full border border-panel-ink-foreground/30 px-6 text-base font-semibold text-panel-ink-foreground transition-colors hover:border-panel-ink-foreground/40 hover:bg-panel-ink-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-panel-ink-foreground/50";

function HomeHeroSkeletonBody() {
  return (
    <div className="mt-4 space-y-3" data-testid="home-hero-skeleton-body">
      <Skeleton className="h-7 w-2/3" rounded="md" />
      <Skeleton className="h-4 w-1/2" rounded="md" />
    </div>
  );
}

function HomeHeroReadinessBody({ onVerify }: { onVerify: () => void }) {
  // Plan 435 M2 UI-fidelity fix item 4b: the gate-off hero shows the same
  // score headline `OverviewScorePanel` renders (the real score number +
  // label), never the bare placeholder label "Your readiness score".
  const { data } = useReadiness();
  const score = data?.result?.score ?? null;
  const label = data?.result?.label ?? "Not yet rated";

  return (
    <div className="mt-4 space-y-3" data-testid="home-hero-readiness-body">
      <p
        className="flex items-baseline gap-2 text-xl font-semibold tracking-tight"
        data-testid="home-hero-headline"
      >
        <span
          className="text-3xl tabular-nums"
          data-testid="home-hero-readiness-score"
        >
          {typeof score === "number" ? score : "—"}
        </span>
        <span className="text-base font-semibold text-panel-ink-foreground/75">
          {label}
        </span>
      </p>
      <p className="text-sm text-panel-ink-foreground/75">
        Verify an input to sharpen your readiness picture.
      </p>
      <Button
        className="mt-1"
        data-testid="home-hero-primary"
        onClick={onVerify}
        size="sm"
      >
        Verify an input
        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function HomeHeroMarketplaceBody({
  state,
  onPrimary,
  onSecondary,
  onGuideClick,
  onCancelConnection,
  bestOffer,
  onBestOffer,
}: HomeHeroMarketplaceProps) {
  const showBestOffer = state.kind === "live_offers" && Boolean(bestOffer);
  // Plan 435 M4 (carried from the M2 review): at `connected` the single
  // avatar is the pro the buyer is actually connected to — name it so the
  // stack renders the pro's initial instead of the generic "A" every other
  // state's anonymous competing-agent avatars fall back to.
  const avatars = Array.from({ length: state.avatarCount }, (_, i) => ({
    id: `home-hero-avatar-${i.toString()}`,
    photoUrl: state.kind === "connected" ? state.proPhotoUrl : undefined,
    name:
      state.kind === "connected" ? state.proName?.trim() || "Agent" : undefined,
  }));
  // Review fix 8: at `connected` the stack is one named person, not a
  // field of anonymous competing agents — the default "N competing agents"
  // label is honest only for the other states.
  const avatarAriaLabel =
    state.kind === "connected" ? state.proName?.trim() || "Agent" : undefined;

  return (
    <>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <p className="headline-2 font-bold" data-testid="home-hero-headline">
            {state.headline}
          </p>
          {showBestOffer ? null : (
            <p
              className="mt-2 text-sm text-panel-ink-foreground/75 sm:text-base"
              data-testid="home-hero-sub"
            >
              {state.sub}
            </p>
          )}
        </div>
        {state.avatarCount > 0 ? (
          <AvatarStack agents={avatars} ariaLabel={avatarAriaLabel} />
        ) : null}
      </div>

      {showBestOffer && bestOffer ? (
        // More prominent than the design's plain sub-line: the best figure
        // is the reason to click "View offers", so it gets its own tinted,
        // tappable block in the accent colour and links straight to it.
        <button
          className="group mt-5 flex w-full max-w-xl flex-col items-start rounded-2xl bg-panel-ink-foreground/[0.06] px-4 py-3 text-left ring-1 ring-panel-ink-accent/25 transition-colors hover:bg-panel-ink-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-panel-ink-accent sm:px-5"
          data-testid="home-hero-best-offer"
          onClick={() => onBestOffer?.(bestOffer.href)}
          type="button"
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-panel-ink-foreground/60">
            Best offer so far
            {bestOffer.proDisplayName ? ` · ${bestOffer.proDisplayName}` : ""}
          </span>
          <span className="mt-1 inline-flex items-center gap-2 text-lg font-semibold text-panel-ink-accent sm:text-xl">
            {bestOffer.line}
            <ArrowRight
              aria-hidden="true"
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
            />
          </span>
        </button>
      ) : null}

      {state.primary || state.secondary ? (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {state.primary ? (
            <button
              className={HERO_PRIMARY_CLASS}
              data-testid="home-hero-primary"
              onClick={onPrimary}
              type="button"
            >
              {state.primary.label}
              {state.kind === "live_offers" ? (
                <span
                  className="inline-flex h-5 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold leading-none text-primary-foreground tabular-nums"
                  data-testid="home-hero-primary-badge"
                >
                  {state.offersCount}
                </span>
              ) : null}
            </button>
          ) : null}

          {state.secondary &&
          state.kind === "finalizing_escape" &&
          state.connectionId ? (
            <CancelConnectionDialog
              connectionId={state.connectionId}
              onCancel={onCancelConnection}
              trigger={(open) => (
                <button
                  className={HERO_SECONDARY_CLASS}
                  data-testid="home-hero-secondary"
                  onClick={open}
                  type="button"
                >
                  {state.secondary?.label}
                </button>
              )}
            />
          ) : state.secondary ? (
            // DS `outline` is dark-text-on-transparent — illegible on the
            // ink panel, so the secondary is an on-ink transparent button
            // (HERO_SECONDARY_CLASS) at the primary's height.
            <button
              className={HERO_SECONDARY_CLASS}
              data-testid="home-hero-secondary"
              onClick={onSecondary}
              type="button"
            >
              {state.secondary.label}
            </button>
          ) : null}
        </div>
      ) : null}
      {state.kind === "connected" ? (
        <a
          className="mt-4 inline-flex text-sm font-medium text-panel-ink-foreground/80 underline-offset-4 hover:text-panel-ink-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-panel-ink-accent"
          data-testid="home-hero-guide-link"
          href={getMarketingUrl("/help/buyers/working-with-an-agent")}
          onClick={onGuideClick}
          rel="noreferrer"
          target="_blank"
        >
          {state.relationshipStage === "closed"
            ? "Working with an agent guide →"
            : "What happens next? →"}
        </a>
      ) : null}
    </>
  );
}
